import React from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Badge,
  Button,
  HStack,
  VStack,
  Avatar,
  Flex,
  Link,
  Icon,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  IconButton,
  Collapse,
  useDisclosure,
} from "@chakra-ui/react";
import {
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { format, isValid } from "date-fns";
import { Pro, PageInfo } from "./types";

interface ProsWithinReachTableProps {
  pros: Pro[];
  isLoading: boolean;
  error?: Error | null;
  pageInfo?: PageInfo;
  totalCount: number;
  onLoadMore?: () => void;
}

export const ProsWithinReachTable: React.FC<ProsWithinReachTableProps> = ({
  pros,
  isLoading,
  error,
  pageInfo,
  totalCount,
  onLoadMore,
}) => {
  const { isOpen: isTableExpanded, onToggle: onToggleTable } = useDisclosure({
    defaultIsOpen: true,
  });

  const formatJoinDate = (dateString: string) => {
    const date = new Date(dateString);
    if (!isValid(date)) return "N/A";
    return format(date, "MMM yyyy");
  };

  const getExperienceDisplay = (years?: number) => {
    if (!years) return "Not specified";
    if (years < 1) return "Less than 1 year";
    if (years === 1) return "1 year";
    return `${Math.floor(years)} years`;
  };


  if (error) {
    return (
      <Alert status='error'>
        <AlertIcon />
        <Box>
          <AlertTitle>Error loading pros!</AlertTitle>
          <AlertDescription>
            Failed to load professionals within reach: {error.message}
          </AlertDescription>
        </Box>
      </Alert>
    );
  }

  return (
    <Box>
      {/* Section Header */}
      <Flex justify='space-between' align='center' mb={4}>
        <VStack align='start' spacing={1}>
          <Text fontSize='lg' fontWeight='bold' color='gray.800'>
            Professionals Within Reach
          </Text>
          <Text fontSize='sm' color='gray.600'>
            {totalCount} professional{totalCount !== 1 ? "s" : ""} match this
            job posting
          </Text>
        </VStack>
        <IconButton
          aria-label={isTableExpanded ? "Collapse table" : "Expand table"}
          icon={isTableExpanded ? <FaChevronUp /> : <FaChevronDown />}
          variant='ghost'
          size='sm'
          onClick={onToggleTable}
        />
      </Flex>

      <Collapse in={isTableExpanded} animateOpacity>
        <Box
          border='1px'
          borderColor='gray.200'
          rounded='lg'
          overflow='hidden'
          bg='white'
        >
          {isLoading && pros.length === 0 ? (
            <Flex justify='center' align='center' py={12}>
              <VStack spacing={3}>
                <Spinner size='lg' color='blue.500' />
                <Text color='gray.500'>Loading professionals...</Text>
              </VStack>
            </Flex>
          ) : pros.length === 0 ? (
            <Flex justify='center' align='center' py={12}>
              <VStack spacing={3}>
                <Text fontSize='lg' fontWeight='medium' color='gray.500'>
                  No professionals found
                </Text>
                <Text fontSize='sm' color='gray.400' textAlign='center'>
                  No professionals match the criteria for this job posting.
                </Text>
              </VStack>
            </Flex>
          ) : (
            <>
              <Table variant='simple'>
                <Thead bg='gray.50'>
                  <Tr>
                    <Th width='35%'>Professional</Th>
                    <Th width='30%'>Contact</Th>
                    <Th width='15%'>Location</Th>
                    <Th width='20%'>Experience</Th>
                    <Th width='15%'>Joined</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {pros.map((pro) => (
                    <Tr key={pro.id} _hover={{ bg: "gray.50" }}>
                      
                      <Td>
                        <HStack spacing={3}>
                          <Avatar
                            size='sm'
                            name={`${pro.firstName} ${pro.lastName}`}
                            bg='blue.500'
                          />
                          <VStack align='start' spacing={0}>
                            <Text fontWeight='medium' fontSize='sm'>
                              {pro.firstName} {pro.lastName}
                            </Text>
                            {pro.jobTitle && (
                              <Text fontSize='xs' color='gray.500'>
                                {pro.jobTitle}
                              </Text>
                            )}
                          </VStack>
                        </HStack>
                      </Td>
                      <Td>
                        <VStack align='start' spacing={1}>
                          <HStack spacing={2}>
                            <Icon
                              as={FaEnvelope}
                              color='gray.400'
                              fontSize='xs'
                            />
                            <Link
                              href={`mailto:${pro.email}`}
                              fontSize='xs'
                              color='blue.600'
                              _hover={{ textDecor: "underline" }}
                            >
                              {pro.email}
                            </Link>
                          </HStack>
                          <HStack spacing={2}>
                            <Icon
                              as={FaPhone}
                              color='gray.400'
                              fontSize='xs'
                            />
                            <Link
                              href={`tel:${pro.phoneNumber}`}
                              fontSize='xs'
                              color='blue.600'
                              _hover={{ textDecor: "underline" }}
                            >
                              {pro.phoneNumber}
                            </Link>
                          </HStack>
                        </VStack>
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          <Icon
                            as={FaMapMarkerAlt}
                            color='gray.400'
                            fontSize='xs'
                          />
                          <Text fontSize='xs' color='gray.600'>
                            {pro.proApplication?.stateOfResidence ||
                              "Not specified"}
                          </Text>
                        </HStack>
                      </Td>
                      <Td>
                        <Text fontSize='xs' color='gray.600'>
                          {getExperienceDisplay(pro.yearsOfExperience)}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize='xs' color='gray.500'>
                          {formatJoinDate(pro.createdAt)}
                        </Text>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>

              {/* Load More Button */}
              {pageInfo?.hasNextPage && (
                <Box p={4} borderTop='1px' borderColor='gray.200' bg='gray.50'>
                  <Flex justify='center'>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={onLoadMore}
                      isLoading={isLoading}
                      loadingText='Loading more...'
                    >
                      Load More Professionals
                    </Button>
                  </Flex>
                </Box>
              )}
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};
